import { BaseArea } from '../base/area/area';
import { BaseInput } from '../base/input/input';
import { BaseSelect } from '../base/select/select';
import React from 'react';
import { AiKeyStatus } from '../../services/ai/ai.derive';
import * as S from './ai.styles';

/** An input value that is a number, or '' while the field is empty (the model types it as a number). */
export const toNumber = (raw: string): number | '' => (raw === '' ? '' : Number(raw));

interface IFieldProps {
    label: React.ReactNode;
    hint?: React.ReactNode;
    error?: string;
}

/**
 * Unique ids that tie a field's visible label (and its hint) to the control, so a screen reader announces what the
 * control is for. `BaseInput`, `BaseArea` and `BaseSelect` do not create that association themselves, and a placeholder
 * is not a name: it disappears on typing and often says only the default value.
 */
export function useFieldIds(hasHint: boolean): { id: string; hintId: string | undefined } {
    const id = React.useId();
    return { id, hintId: hasHint ? `${id}-hint` : undefined };
}

export const NumberField = ({
    label,
    hint,
    error,
    value,
    onChange,
    ...rest
}: IFieldProps & {
    value: number | string | undefined;
    onChange: (value: number | '') => void;
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
}): React.ReactElement => {
    const { id, hintId } = useFieldIds(!!hint);
    return (
        <S.Field>
            <S.Label htmlFor={id}>{label}</S.Label>
            <BaseInput
                id={id}
                aria-describedby={hintId}
                type="number"
                value={value ?? ''}
                errorMessage={error}
                onChange={(e): void => onChange(toNumber(e.target.value))}
                {...rest}
            />
            {hint && <S.Hint id={hintId}>{hint}</S.Hint>}
        </S.Field>
    );
};

export const TextField = ({
    label,
    hint,
    error,
    value,
    onChange,
    ...rest
}: IFieldProps & {
    value: string | undefined;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    mono?: boolean;
}): React.ReactElement => {
    const { id, hintId } = useFieldIds(!!hint);
    return (
        <S.Field>
            <S.Label htmlFor={id}>{label}</S.Label>
            <BaseInput
                id={id}
                aria-describedby={hintId}
                value={value ?? ''}
                errorMessage={error}
                onChange={(e): void => onChange(e.target.value)}
                {...rest}
            />
            {hint && <S.Hint id={hintId}>{hint}</S.Hint>}
        </S.Field>
    );
};

export const AreaField = ({
    label,
    hint,
    value,
    onChange,
    rows = 2,
}: IFieldProps & { value: string | undefined; onChange: (value: string) => void; rows?: number }): React.ReactElement => {
    const { id, hintId } = useFieldIds(!!hint);
    return (
        <S.Field>
            <S.Label htmlFor={id}>{label}</S.Label>
            <BaseArea
                id={id}
                aria-describedby={hintId}
                rows={rows}
                value={value ?? ''}
                onChange={(e): void => onChange(e.target.value)}
            />
            {hint && <S.Hint id={hintId}>{hint}</S.Hint>}
        </S.Field>
    );
};

export interface IOption<T extends string> {
    value: T;
    label: string;
}

export function SelectField<T extends string>({
    label,
    hint,
    error,
    value,
    options,
    onChange,
    disabled,
}: IFieldProps & {
    value: T | undefined;
    options: Array<IOption<T>>;
    onChange: (value: T) => void;
    disabled?: boolean;
}): React.ReactElement {
    const { id } = useFieldIds(false);
    return (
        <S.Field>
            <S.Label htmlFor={id}>{label}</S.Label>
            {/* `required` stops react-select offering a clear button: these always hold a value. `inputId` puts the id on
                the select's own text input, which is what the label points at. */}
            <BaseSelect
                inputId={id}
                required={true}
                disabled={disabled}
                options={options}
                value={options.find(o => o.value === value) ?? null}
                errorMessage={error}
                onChange={(option: unknown): void => {
                    const picked = (option as IOption<T> | null)?.value;
                    if (picked !== undefined) onChange(picked);
                }}
            />
            {hint && <S.Hint>{hint}</S.Hint>}
        </S.Field>
    );
}

export const KEY_STATUS_TONE: Record<AiKeyStatus, S.TagTone> = {
    ACTIVE: 'success',
    REVOKED: 'danger',
    EXPIRED: 'neutral',
};

export const TargetTag = ({ type }: { type: 'MODEL' | 'AGENT' | string }): React.ReactElement => (
    <S.Tag tone={type === 'AGENT' ? 'primary' : 'neutral'}>{type}</S.Tag>
);

export const StatusTag = ({ tone, children }: { tone: S.TagTone; children: React.ReactNode }): React.ReactElement => (
    <S.Tag tone={tone}>
        <S.Dot tone={tone} />
        {children}
    </S.Tag>
);

/** Format an ISO timestamp for a table cell; a value that does not parse is shown as it came, not as "Invalid Date". */
export function formatWhen(iso: string | undefined): string {
    if (!iso) return '—';
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return iso;
    return new Date(t).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
}
